if (!customElements.get('byte-product-split-slider')) {
  class ByteProductSplitSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.visibleCount = 5;
      this.slideCount = 0;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);

      // Drag state
      this._drag = {
        active: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        startIndex: 0,
        didMove: false,
        isTouch: false,
      };

      this.boundPointerDown = this._onPointerDown.bind(this);
      this.boundPointerMove = this._onPointerMove.bind(this);
      this.boundPointerUp = this._onPointerUp.bind(this);

      this.resizeObserver = null;
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.viewport || !this.track || this.slideCount === 0) return;

      this.#buildDots();
      this.#bindEvents();
      this.#updateLayout();

      this.resizeObserver = new ResizeObserver(() => {
        this.#updateLayout();
      });
      this.resizeObserver.observe(this.viewport);
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    handleClick(event) {
      // Drag hareketi olduysa click'i yutuyoruz (sadece track içindeki elementler için)
      if (this._drag.didMove && this.track && this.track.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const actionTrigger = event.target.closest('[data-product-split-action]');
      if (actionTrigger && !actionTrigger.hasAttribute('disabled')) {
        const action = actionTrigger.dataset.productSplitAction;
        if (action === 'prev') { event.preventDefault(); this.#goTo(this.currentIndex - 1); }
        if (action === 'next') { event.preventDefault(); this.#goTo(this.currentIndex + 1); }
        return;
      }

      const dot = event.target.closest('[data-product-split-dot]');
      if (dot) {
        event.preventDefault();
        const index = parseInt(dot.dataset.productSplitDot, 10);
        if (!isNaN(index)) this.#goTo(index);
      }
    }

    handleResize() {
      this.#updateLayout();
    }

    // ─── Drag / Touch handlers ────────────────────────────────────────────────

    _onPointerDown(event) {
      // Sadece sol tık veya dokunma
      if (event.type === 'mousedown' && event.button !== 0) return;

      const isTouch = event.type === 'touchstart';
      const clientX = isTouch ? event.touches[0].clientX : event.clientX;
      const clientY = isTouch ? event.touches[0].clientY : event.clientY;

      this._drag.active = true;
      this._drag.startX = clientX;
      this._drag.startY = clientY;
      this._drag.currentX = clientX;
      this._drag.startIndex = this.currentIndex;
      this._drag.didMove = false;
      this._drag.isTouch = isTouch;

      this.track.classList.remove('is-animating');

      if (isTouch) {
        window.addEventListener('touchmove', this.boundPointerMove, { passive: false });
        window.addEventListener('touchend', this.boundPointerUp);
        window.addEventListener('touchcancel', this.boundPointerUp);
      } else {
        window.addEventListener('mousemove', this.boundPointerMove);
        window.addEventListener('mouseup', this.boundPointerUp);
      }
    }

    _onPointerMove(event) {
      if (!this._drag.active) return;

      const isTouch = event.type === 'touchmove';
      const clientX = isTouch ? event.touches[0].clientX : event.clientX;
      const clientY = isTouch ? event.touches[0].clientY : event.clientY;

      const deltaX = clientX - this._drag.startX;
      const deltaY = clientY - this._drag.startY;

      // Dikey kaydırma baskınsa slider'ı devretme
      if (!this._drag.didMove && Math.abs(deltaY) > Math.abs(deltaX)) {
        this._drag.active = false;
        this._cleanup();
        return;
      }

      // currentX'i her zaman güncelle (didMove öncesinde de)
      this._drag.currentX = clientX;

      if (Math.abs(deltaX) > 4) {
        this._drag.didMove = true;
      }

      if (!this._drag.didMove) return;

      // Yatay scroll'u engelle
      event.preventDefault();

      // Gerçek zamanlı track hareketi
      const stride = this.#getSlideStride();
      const maxOffset = this._drag.startIndex * stride;
      const newOffset = maxOffset - deltaX;
      this.track.style.transform = `translate3d(-${newOffset}px, 0, 0)`;
    }

    _onPointerUp(event) {
      if (!this._drag.active) return;

      // touchend'de touches[] boş olur, changedTouches kullan
      if (event && event.type === 'touchend' && event.changedTouches?.length) {
        this._drag.currentX = event.changedTouches[0].clientX;
      }

      this._drag.active = false;
      this._cleanup();

      if (!this._drag.didMove) return;

      // didMove'u bir sonraki tick'te sıfırla — aynı cycle'daki track click'ini yutar
      // ama dot/nav gibi dışarıdaki elementlere gelen sonraki click'leri etkilemez
      setTimeout(() => { this._drag.didMove = false; }, 0);

      const deltaX = this._drag.currentX - this._drag.startX;
      const stride = this.#getSlideStride();

      // Threshold bazlı: stride'ın %20'sini geçmişse 1 slide, geçmemişse snap back
      let slidesMoved = 0;
      const absDelta = Math.abs(deltaX);
      if (absDelta > stride * 0.2) {
        slidesMoved = deltaX < 0
          ? Math.ceil(absDelta / stride)
          : -Math.ceil(absDelta / stride);
      }

      this.#goTo(this._drag.startIndex + slidesMoved);
    }

    _cleanup() {
      window.removeEventListener('mousemove', this.boundPointerMove);
      window.removeEventListener('mouseup', this.boundPointerUp);
      window.removeEventListener('touchmove', this.boundPointerMove);
      window.removeEventListener('touchend', this.boundPointerUp);
      window.removeEventListener('touchcancel', this.boundPointerUp);
    }

    // ─── Slider logic ─────────────────────────────────────────────────────────

    #goTo(index) {
      const maxIndex = Math.max(0, this.slideCount - this.visibleCount);
      this.currentIndex = Math.min(maxIndex, Math.max(0, index));
      this.#applyTransform(true);
      this.#updateNavState();
      this.#updateDots();
    }

    #getVisibleCount() {
      const columnsValue = getComputedStyle(this).getPropertyValue('--byte-pss-columns').trim();
      const parsedColumns = parseInt(columnsValue, 10);
      return Number.isNaN(parsedColumns) || parsedColumns < 1 ? 1 : parsedColumns;
    }

    #getGap() {
      if (!this.track) return 0;
      const gapValue = getComputedStyle(this.track).columnGap || getComputedStyle(this).getPropertyValue('--byte-pss-gap');
      const parsedGap = parseFloat(gapValue);
      return Number.isNaN(parsedGap) ? 0 : parsedGap;
    }

    #getViewportContentWidth() {
      const viewportStyles = getComputedStyle(this.viewport);
      const paddingInline =
        (parseFloat(viewportStyles.paddingLeft) || 0) + (parseFloat(viewportStyles.paddingRight) || 0);
      return this.viewport.clientWidth - paddingInline;
    }

    #getSlideWidth() {
      const viewportContentWidth = this.#getViewportContentWidth();
      if (viewportContentWidth <= 0) return 0;
      const totalGap = this.#getGap() * (this.visibleCount - 1);
      return Math.floor((viewportContentWidth - totalGap) / this.visibleCount);
    }

    #getSlideStride() {
      return this.#getSlideWidth() + this.#getGap();
    }

    #updateLayout() {
      this.visibleCount = this.#getVisibleCount();
      const maxIndex = Math.max(0, this.slideCount - this.visibleCount);

      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }

      const slideWidth = this.#getSlideWidth();

      if (slideWidth > 0) {
        const slideWidthPx = `${slideWidth}px`;
        for (const slide of this.slides) {
          slide.style.setProperty('--byte-pss-slide-width', slideWidthPx);
          slide.style.flexBasis = slideWidthPx;
          slide.style.width = slideWidthPx;
          slide.style.maxWidth = slideWidthPx;
        }
      }

      this.#rebuildDots();
      this.#applyTransform(false);
      this.#updateNavState();
      this.#updateDots();
    }

    #applyTransform(shouldAnimate) {
      const slideStride = this.#getSlideStride();
      if (slideStride <= 0) return;

      const offsetPx = this.currentIndex * slideStride;

      if (shouldAnimate) {
        this.track.classList.add('is-animating');
      } else {
        this.track.classList.remove('is-animating');
      }

      this.track.style.transform = `translate3d(-${offsetPx}px, 0, 0)`;
    }

    #updateNavState() {
      const maxIndex = Math.max(0, this.slideCount - this.visibleCount);
      const isAtStart = this.currentIndex <= 0;
      const isAtEnd = this.currentIndex >= maxIndex;

      if (this.prevButton) {
        this.prevButton.disabled = isAtStart;
        this.prevButton.classList.toggle('is-disabled', isAtStart);
        this.prevButton.setAttribute('aria-disabled', String(isAtStart));
      }

      if (this.nextButton) {
        this.nextButton.disabled = isAtEnd;
        this.nextButton.classList.toggle('is-disabled', isAtEnd);
        this.nextButton.setAttribute('aria-disabled', String(isAtEnd));
      }
    }

    #totalDots() {
      return Math.max(1, this.slideCount - this.visibleCount + 1);
    }

    #buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      const total = this.#totalDots();

      if (total <= 1) {
        this.dotsContainer.hidden = true;
        return;
      }

      this.dotsContainer.hidden = false;

      for (let i = 0; i < total; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'byte-product-split-slider__dot';
        btn.dataset.productSplitDot = i;
        btn.setAttribute('aria-label', `${i + 1} / ${total}`);
        this.dotsContainer.appendChild(btn);
      }
    }

    #rebuildDots() {
      this.#buildDots();
    }

    #updateDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.querySelectorAll('[data-product-split-dot]').forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    #cacheElements() {
      this.viewport = this.querySelector('[data-product-split-viewport]');
      this.track = this.querySelector('[data-product-split-track]');
      this.slides = this.track ? [...this.track.querySelectorAll('[data-product-split-slide]')] : [];
      this.slideCount = this.slides.length;
      this.prevButton = this.querySelector('[data-product-split-action="prev"]');
      this.nextButton = this.querySelector('[data-product-split-action="next"]');
      this.dotsContainer = this.querySelector('[data-product-split-dots]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      window.addEventListener('resize', this.boundHandleResize);

      if (this.track) {
        this.track.addEventListener('mousedown', this.boundPointerDown);
        this.track.addEventListener('touchstart', this.boundPointerDown, { passive: true });
        // Drag sırasında text/image seçimini engelle
        this.track.style.userSelect = 'none';
        this.track.style.webkitUserSelect = 'none';
      }
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      window.removeEventListener('resize', this.boundHandleResize);

      if (this.track) {
        this.track.removeEventListener('mousedown', this.boundPointerDown);
        this.track.removeEventListener('touchstart', this.boundPointerDown);
      }

      this._cleanup();
    }
  }

  customElements.define('byte-product-split-slider', ByteProductSplitSlider);
}